import { AxiosResponse } from 'axios';
import { Module } from '../../../api/module';
import { ModulePublishingState } from '../../../@types/module-publishing-state';
import api from '../../../api';
import { replaceSoftHyphenWithUnicode } from '../../../utils/replaceSoftHyphenWithUnicode';

export type UserModule = {
  assignments: number;
  externalContentUrl?: string;
  hostedExternally: boolean;
  id: string;
  isDemo: boolean;
  incomplete: boolean;
  image: string;
  niceName: string;
  publishingState: ModulePublishingState;
  releaseDate?: string;
  slug: string;
  subjectName: string;
  years: string[];
  subModules: string[];
  subjectSlug: string;
  methodSlug: string;
  catalogName: string;
};

export type Subject = {
  id: string;
  name: string;
  slug: string;
  published: boolean;
};

export type Method = {
  catalogName: string;
  educationalTypes: string[];
  externalContentUrl?: string;
  hostedExternally?: boolean;
  id: string;
  logo?: string;
  name: string;
  published: boolean;
  slug: string;
  subjectId: string;
  weight: number;
};

export async function fetchUserModules(studioGroup: StudioGroup): Promise<UserModule[]> {
  const {
    data: { data },
  } = await api.get<AxiosResponse<UserModule[]>>('/studio/user/modules', {
    params: {
      group: studioGroup,
    },
  });

  data.forEach(module => {
    module.catalogName = replaceSoftHyphenWithUnicode(module.catalogName);
  });

  return data;
}

export async function fetchVisitedModules(): Promise<string[]> {
  const {
    data: { data },
  } = await api.get<AxiosResponse<string[]>>('/studio/user/modules/visited');

  return data;
}

/**
 * Fetches the list of subjects for the specified studio group.
 *
 * The returned subjects are pre-sorted by their `slug` field in ascending order.
 *
 * @param {StudioGroup} group - The studio group to fetch subjects for.
 * @returns {Promise<Subject[]>} A promise that resolves to a list of subjects sorted by slug.
 */
export async function fetchSubjects(group: StudioGroup) {
  const {
    data: { data: subjects },
  } = await api.get<AxiosResponse<Subject[]>>('/studio/subjects', { params: { group } });

  return subjects;
}

export async function fetchMethods(subjectIds: string[]) {
  const {
    data: { data: methods },
  } = await api.get<AxiosResponse<Method[]>>('/studio/methods', { params: { subjects: subjectIds } });

  methods.forEach(method => {
    method.catalogName = replaceSoftHyphenWithUnicode(method.catalogName);
  });

  return methods;
}

export async function fetchModules(methodId: string) {
  const {
    data: { data: modules },
  } = await api.get<AxiosResponse<Module[]>>('/studio/modules', { params: { methodId } });

  modules.forEach(module => {
    module.catalogName = replaceSoftHyphenWithUnicode(module.catalogName);
  });

  return modules;
}

interface PinnedModule {
  id: number;
  moduleId: string;
}

export async function fetchPinnedModules(): Promise<PinnedModule[]> {
  const {
    data: { data },
  } = await api.get<AxiosResponse<PinnedModule[]>>('/studio/user/modules/pinned');

  return data;
}

export async function savePinnedModule(moduleId: string): Promise<PinnedModule> {
  const { data } = await api.post<PinnedModule>('/studio/user/modules/pinned', { moduleId });

  return data;
}

export async function removePinnedModule(pinnedModuleId: number) {
  await api.delete<PinnedModule>(`/studio/user/modules/pinned/${pinnedModuleId}`);
}
